import React from 'react';
import { 
  Sparkles, 
  LogIn, 
  Syringe, 
  ShieldCheck, 
  RefreshCw, 
  History, 
  Pill, 
  Flame, 
  ArrowRight,
  CheckCircle2,
  Lock,
  Layers,
  Calendar
} from 'lucide-react';

export default function LandingHero({ onOpenAuth, onExploreTools, onExploreCalendar }) {
  const sampleCompounds = [
    { name: "Tirzepatide", category: "Peptide", dose: "4 mg / wk", color: "purple" },
    { name: "Testosterone Enanthate", category: "Hormones & AAS", dose: "100 mg / wk", color: "rose" },
    { name: "NMN + TMG", category: "Supplement", dose: "1000 mg / day", color: "sky" },
    { name: "GHK-Cu", category: "Peptide", dose: "2 mg / day", color: "purple" },
    { name: "Vitamin D3 + K2", category: "Supplement", dose: "125 mcg / day", color: "sky" },
    { name: "CJC-1295 / Ipamorelin", category: "Peptide", dose: "150 mcg / dose", color: "purple" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 sm:py-12 text-slate-100">
      
      {/* Hero Header Section */}
      <div className="relative rounded-3xl bg-gradient-to-b from-slate-900/90 via-slate-900/60 to-slate-950/80 border border-slate-800 p-8 sm:p-12 lg:p-16 overflow-hidden shadow-2xl mb-12">
        {/* Ambient Glowing Orbs */}
        <div className="absolute top-0 right-0 -mr-24 -mt-24 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-24 -mb-24 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-3xl">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-semibold uppercase tracking-wider mb-6">
            <Sparkles className="w-3.5 h-3.5 animate-pulse text-sky-400" />
            <span>Personal Protocol Stack Intelligence</span>
          </div>

          {/* Headline */}
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight sm:leading-none mb-6">
            Track, Calibrate & Master Your <span className="bg-gradient-to-r from-sky-400 via-teal-300 to-emerald-400 bg-clip-text text-transparent">Supplements & Peptides</span>
          </h1>

          {/* Subtitle */}
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl">
            A privacy-first regimen tracking platform with real-time multi-device sync, immutable audit logging, stack snapshot checkpoints, and an interactive U-100 peptide reconstitution engine.
          </p>

          {/* Primary CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5">
            <button
              onClick={onOpenAuth}
              className="flex items-center justify-center space-x-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-400 hover:to-indigo-500 text-white font-bold text-sm shadow-xl shadow-sky-500/25 hover:shadow-sky-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              <LogIn className="w-4 h-4" />
              <span>Sign In to Build Your Stack</span>
              <ArrowRight className="w-4 h-4 ml-1" />
            </button>

            <a
              href="/calendar"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
                e.preventDefault();
                onExploreCalendar();
              }}
              className="flex items-center justify-center space-x-2 px-6 py-3.5 rounded-2xl bg-slate-800/80 hover:bg-slate-700/80 text-slate-200 hover:text-white font-semibold text-sm border border-slate-700/80 hover:border-slate-600 transition-all hover:scale-[1.01] active:scale-[0.99] cursor-pointer"
            >
              <Calendar className="w-4 h-4 text-sky-400" />
              <span>View Regimen Calendar</span>
            </a>

            <a
              href="/tools"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
                e.preventDefault();
                onExploreTools();
              }}
              className="flex items-center justify-center space-x-2 px-6 py-3.5 rounded-2xl bg-slate-800/80 hover:bg-slate-700/80 text-slate-200 hover:text-white font-semibold text-sm border border-slate-700/80 hover:border-slate-600 transition-all hover:scale-[1.01] active:scale-[0.99] cursor-pointer"
            >
              <Syringe className="w-4 h-4 text-emerald-400" />
              <span>Try Peptide Calculator</span>
            </a>
          </div>

          {/* Trust Highlights */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 pt-8 border-t border-slate-800/80 text-xs text-slate-400">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span>Private Database Isolation</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span>Live Multi-Device Sync</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span>Instant Sample Template</span>
            </div>
          </div>
        </div>
      </div>

      {/* Feature Pillars Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        
        {/* Pillar 1: Stack Tracking */}
        <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-sky-500/40 transition-all group">
          <div className="p-3 rounded-2xl bg-sky-500/10 text-sky-400 border border-sky-500/20 w-fit mb-4 group-hover:scale-105 transition-transform">
            <Layers className="w-6 h-6" />
          </div>
          <h3 className="text-base font-bold text-white mb-2">Protocol Stack Tracking</h3>
          <p className="text-xs text-slate-400 leading-relaxed">
            Organize Supplements, Peptides, and Hormones & AAS with dosage ranges, units (mg, mcg, IU), timing of day, and frequency schedules.
          </p>
        </div>

        {/* Pillar 2: Peptide Calculator */}
        <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-emerald-500/40 transition-all group">
          <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 w-fit mb-4 group-hover:scale-105 transition-transform">
            <Syringe className="w-6 h-6" />
          </div>
          <h3 className="text-base font-bold text-white mb-2">Reconstitution Engine</h3>
          <p className="text-xs text-slate-400 leading-relaxed">
            Calibrate dry peptide mass and diluent volume into exact U-100 syringe units with interactive SVG visuals and vial capacity safety guards.
          </p>
        </div>

        {/* Pillar 3: Real-Time Sync */}
        <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-purple-500/40 transition-all group">
          <div className="p-3 rounded-2xl bg-purple-500/10 text-purple-400 border border-purple-500/20 w-fit mb-4 group-hover:scale-105 transition-transform">
            <RefreshCw className="w-6 h-6" />
          </div>
          <h3 className="text-base font-bold text-white mb-2">Real-Time Cloud Sync</h3>
          <p className="text-xs text-slate-400 leading-relaxed">
            Your protocol stack updates instantaneously across phones, tablets, and desktop browsers without page reloads.
          </p>
        </div>

        {/* Pillar 4: Audit Logs & Checkpoints */}
        <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-amber-500/40 transition-all group">
          <div className="p-3 rounded-2xl bg-amber-500/10 text-amber-400 border border-amber-500/20 w-fit mb-4 group-hover:scale-105 transition-transform">
            <History className="w-6 h-6" />
          </div>
          <h3 className="text-base font-bold text-white mb-2">Checkpoints & Recovery</h3>
          <p className="text-xs text-slate-400 leading-relaxed">
            Automated change logs track every modification. Create stack snapshots and restore previous cycles with a single click.
          </p>
        </div>

      </div>

      {/* Sample Template Teaser Banner */}
      <div className="rounded-3xl bg-slate-900/70 border border-slate-800 p-6 sm:p-8 relative overflow-hidden">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-6">
          <div>
            <div className="inline-flex items-center space-x-2 text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-1">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Pre-Engineered Baseline</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              Instant Setup with Research-Backed Sample Template
            </h2>
            <p className="text-xs text-slate-400 mt-1 max-w-xl">
              Sign up today and initialize your stack with 14 curated compounds across longevity supplements, GLP-1/GIP and growth hormone secretagogue peptides, and calibrated TRT protocols.
            </p>
          </div>

          <button
            onClick={onOpenAuth}
            className="flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-bold text-xs shadow-lg shadow-emerald-500/20 transition-all whitespace-nowrap"
          >
            <span>Sign In to Load Template</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Sample Pill Badges */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 pt-4 border-t border-slate-800/80">
          {sampleCompounds.map((item, idx) => (
            <div 
              key={idx}
              className="p-3 rounded-xl bg-slate-950/70 border border-slate-800/90 flex flex-col justify-between"
            >
              <div>
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${
                  item.color === 'purple' ? 'text-purple-400' :
                  item.color === 'rose' ? 'text-rose-400' : 'text-sky-400'
                }`}>
                  {item.category}
                </span>
                <div className="text-xs font-bold text-white mt-0.5 truncate" title={item.name}>
                  {item.name}
                </div>
              </div>
              <div className="text-[11px] font-mono text-slate-400 mt-2">
                {item.dose}
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
